<script setup lang="ts">
import type { RouteLocationRaw, RouteRecordNameGeneric } from 'vue-router'

import type { SubItem } from '@/types/types'
import type { Icons } from '@/components/base/BaseIcon.vue'

const props = withDefaults(
  defineProps<{
    /** Route names that make this item active (for link or dropdown) */
    activeRoutes: RouteRecordNameGeneric[]
    /** Display label (e.g. translated sidebar key) */
    name: string
    /** Icon name for BaseIcon */
    icon: Icons
    iconAccent?: boolean
    /** When true, render as button that emits openUpsell instead of navigating */
    showUpsellModal?: boolean
    /** Which upsell module to show when showUpsellModal is true */
    upsellModuleType?: 'e-reputation' | 'pongo-suite'
    /** For dropdown: whether the submenu is expanded */
    isDropdownOpen?: boolean
    /** For link: route to navigate to. Omit when using dropdown or upsell button. */
    to?: RouteLocationRaw
    /** For dropdown: submenu items. Omit for simple link or upsell button. */
    subItems?: SubItem[]
  }>(),
  {
    showUpsellModal: false,
    upsellModuleType: 'e-reputation',
    isDropdownOpen: false,
    iconAccent: false
  }
)

const emit = defineEmits<{
  (e: 'toggleDropdown'): void
  (e: 'openUpsell', type: 'e-reputation' | 'pongo-suite'): void
}>()

const route = useRoute()

const activeClass = computed(() => {
  return 'bg-nav-active-bg font-medium text-nav-active-text! rounded-lg hover:bg-nav-active-hover!'
})
const iconClass = computed(() => {
  return ['h-5 w-5 text-primary', props.iconAccent && 'icon-accent']
})
const menuItemClass = computed(() => {
  return 'group flex! items-center px-3 py-1.5 text-sm text-foreground transition-colors duration-200 ease-out hover:rounded-lg hover:bg-nav-hover'
})
const isActive = computed(() => {
  return props.activeRoutes.includes(route.name)
})

const iconName = computed<Icons>(() =>
  isActive.value ? (props.icon.replace(/IconOutline$/, 'IconSolid24') as Icons) : props.icon
)

function onUpsellClick() {
  emit('openUpsell', props.upsellModuleType)
}
</script>

<template>
  <button
    v-if="showUpsellModal"
    type="button"
    :class="[menuItemClass, upsellModuleType !== 'e-reputation' && 'opacity-50', 'w-full text-left']"
    @click="onUpsellClick"
  >
    <BaseIcon :name="icon" :class="iconClass" />
    <div class="ml-3 flex-1">
      {{ name }}
    </div>
    <BaseIcon
      v-if="upsellModuleType !== 'e-reputation'"
      name="HeroLockClosedIconOutline"
      class="size-4 text-muted-foreground"
    />
  </button>

  <!-- Simple link -->
  <router-link
    v-if="!showUpsellModal && to && (subItems?.length ?? 0) === 0"
    :to="to"
    :class="[menuItemClass, isActive ? activeClass : '']"
    :active-class="activeClass"
  >
    <BaseIcon :name="iconName" :class="iconClass" />
    <div class="ml-3 flex-1 text-left">
      <span>{{ name }}</span>
    </div>
  </router-link>

  <!-- Dropdown with subitems -->
  <SidebarDropdownItem
    v-else-if="!showUpsellModal && subItems?.length"
    :menu-name="name"
    :is-dropdown-open="isDropdownOpen"
    :icon="icon"
    :is-menu-active="isActive"
    :subitems="subItems"
    @toggle-dropdown="emit('toggleDropdown')"
  />
</template>

<style scoped>
:deep(.icon-accent[data-icon-name$='Solid24'] svg) {
  fill: url(#assistantIconGradient) currentColor;
}
</style>
